/**
 * Harmonic Drift - Canvas Renderer
 * Handles all canvas-based rendering
 */

import { CONFIG } from '../config/index.js';

class CanvasRenderer {
    constructor() {
        this.canvas = null;
        this.ctx = null;
        this.width = 0;
        this.height = 0;
        this.bgPulse = 0;
    }

    /**
     * Initialize the renderer with a canvas element
     * @param {HTMLCanvasElement} canvas - Canvas element
     */
    init(canvas) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d');
        this.resize();
        
        window.addEventListener('resize', () => this.resize());
    }

    /**
     * Handle resize events
     */
    resize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        this.canvas.width = this.width;
        this.canvas.height = this.height;
    }

    /**
     * Get canvas dimensions
     * @returns {Object} { width, height }
     */
    getDimensions() {
        return { width: this.width, height: this.height };
    }

    /**
     * Get canvas bounding rect
     * @returns {DOMRect} Canvas bounding rect
     */
    getBoundingRect() {
        return this.canvas.getBoundingClientRect();
    }

    /**
     * Trigger a background pulse effect
     */
    triggerPulse() {
        this.bgPulse = 1;
    }

    /**
     * Clear and draw background
     */
    drawBackground() {
        if (this.bgPulse > 0) this.bgPulse -= 0.05;
        
        this.ctx.fillStyle = `rgb(5, 5, ${8 + this.bgPulse * 10})`;
        this.ctx.fillRect(0, 0, this.width, this.height);

        // Draw pulse ring
        if (this.bgPulse > 0.1) {
            this.ctx.beginPath();
            this.ctx.arc(
                this.width / 2, 
                this.height / 2, 
                this.width * this.bgPulse * 0.5, 
                0, 
                Math.PI * 2
            );
            this.ctx.strokeStyle = `rgba(0, 243, 255, ${this.bgPulse * 0.05})`;
            this.ctx.lineWidth = 50;
            this.ctx.stroke();
        }
    }

    /**
     * Draw connection lines between nodes
     * @param {Array<Node>} nodes - Array of nodes
     */
    drawConnections(nodes) {
        this.ctx.lineWidth = 1;
        
        for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
                const dx = nodes[i].x - nodes[j].x;
                const dy = nodes[i].y - nodes[j].y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                
                if (dist < CONFIG.connectionDistance) {
                    const alpha = 1 - (dist / CONFIG.connectionDistance);
                    this.ctx.strokeStyle = `rgba(255, 255, 255, ${alpha * 0.2})`;
                    this.ctx.beginPath();
                    this.ctx.moveTo(nodes[i].x, nodes[i].y);
                    this.ctx.lineTo(nodes[j].x, nodes[j].y);
                    this.ctx.stroke();
                }
            }
        }
    }

    /**
     * Draw all nodes
     * @param {Array<Node>} nodes - Array of nodes
     * @param {Object} mouse - Mouse state (for locked nodes)
     */
    drawNodes(nodes, mouse) {
        this.ctx.globalCompositeOperation = 'lighter';
        
        nodes.forEach(node => {
            this.drawNode(node, mouse);
        });
        
        this.ctx.globalCompositeOperation = 'source-over';
    }

    /**
     * Draw a single node
     * @param {Node} node - Node to draw
     * @param {Object} mouse - Mouse state
     */
    drawNode(node, mouse) {
        const scale = 1 + node.spawning;
        
        // Draw glow
        this.ctx.beginPath();
        const glowSize = (node.radius + (node.pulse * 20)) * scale;
        const gradient = this.ctx.createRadialGradient(
            node.x, node.y, node.radius * 0.2,
            node.x, node.y, glowSize
        );
        gradient.addColorStop(0, node.baseColor);
        gradient.addColorStop(1, 'rgba(0,0,0,0)');
        
        this.ctx.fillStyle = gradient;
        this.ctx.globalAlpha = node.instDef.behavior === 'floaty' ? 0.3 : 0.6;
        this.ctx.arc(node.x, node.y, glowSize, 0, Math.PI * 2);
        this.ctx.fill();

        // Draw connection indicators for looping instruments
        if (node.isLooping && node.connectionCount > 0) {
            this.drawConnectionIndicator(node, scale);
        }

        // Draw looping pattern ring for looping instruments
        if (node.isLooping) {
            this.drawPatternRing(node, scale);
        }

        // Draw center
        this.ctx.beginPath();
        this.ctx.globalAlpha = 1;
        this.ctx.fillStyle = '#fff';
        
        // Different shapes for different instrument types
        if (node.instDef.toneType === 'MembraneSynth' || 
            node.instDef.toneType === 'MetalSynth') {
            // Square for percussion
            const s = (node.radius * 0.4) * scale;
            this.ctx.rect(node.x - s, node.y - s, s * 2, s * 2);
        } else if (node.isLooping) {
            // Diamond for looping instruments
            const s = (node.radius * 0.35) * scale;
            this.ctx.moveTo(node.x, node.y - s);
            this.ctx.lineTo(node.x + s, node.y);
            this.ctx.lineTo(node.x, node.y + s);
            this.ctx.lineTo(node.x - s, node.y);
            this.ctx.closePath();
        } else {
            // Circle for regular instruments
            this.ctx.arc(
                node.x, node.y, 
                (node.radius * 0.25) * scale, 
                0, Math.PI * 2
            );
        }
        this.ctx.fill();

        // Draw drag line when locked
        if (node.locked && mouse) {
            this.ctx.strokeStyle = '#fff';
            this.ctx.lineWidth = 1;
            this.ctx.beginPath();
            this.ctx.moveTo(node.x, node.y);
            this.ctx.lineTo(mouse.x, mouse.y);
            this.ctx.stroke();
        }

        // Draw note label during pulse
        if (node.pulse > 0.1) {
            this.ctx.fillStyle = '#fff';
            this.ctx.font = '10px monospace';
            this.ctx.textAlign = 'center';
            this.ctx.fillText(node.lastNote, node.x, node.y - node.radius - 10);
        }

        // Draw velocity indicator for modulated instruments
        if (node.physicsModulation && Object.keys(node.physicsModulation).length > 0) {
            this.drawVelocityIndicator(node, scale);
        }
    }

    /**
     * Draw a ring showing the looping pattern progress
     * @param {Node} node - Node to draw
     * @param {number} scale - Scale factor
     */
    drawPatternRing(node, scale) {
        const pattern = node.patternName;
        const patternData = node.instDef.pattern ? 
            (typeof node.instDef.pattern === 'string' ? null : node.instDef.pattern) : null;
        
        const ringRadius = (node.radius + 5) * scale;
        const patternLength = 8; // Visual segments
        
        this.ctx.strokeStyle = node.baseColor;
        this.ctx.lineWidth = 2;
        this.ctx.globalAlpha = 0.6;
        
        // Draw pattern segments
        for (let i = 0; i < patternLength; i++) {
            const startAngle = (i / patternLength) * Math.PI * 2 - Math.PI / 2;
            const endAngle = ((i + 0.7) / patternLength) * Math.PI * 2 - Math.PI / 2;
            
            // Highlight current pattern position
            const isCurrent = (node.patternIndex % patternLength) === i;
            this.ctx.globalAlpha = isCurrent ? 1 : 0.3;
            
            this.ctx.beginPath();
            this.ctx.arc(node.x, node.y, ringRadius, startAngle, endAngle);
            this.ctx.stroke();
        }
        
        this.ctx.globalAlpha = 1;
    }

    /**
     * Draw connection indicator showing number of connections
     * @param {Node} node - Node to draw
     * @param {number} scale - Scale factor
     */
    drawConnectionIndicator(node, scale) {
        const count = Math.min(node.connectionCount, 5);
        const indicatorRadius = (node.radius + 15) * scale;
        
        this.ctx.globalAlpha = 0.8;
        
        for (let i = 0; i < count; i++) {
            const angle = (i / 5) * Math.PI * 2 - Math.PI / 2;
            const dotX = node.x + Math.cos(angle) * indicatorRadius;
            const dotY = node.y + Math.sin(angle) * indicatorRadius;
            
            this.ctx.beginPath();
            this.ctx.fillStyle = node.baseColor;
            this.ctx.arc(dotX, dotY, 3, 0, Math.PI * 2);
            this.ctx.fill();
        }
        
        this.ctx.globalAlpha = 1;
    }

    /**
     * Draw velocity indicator showing current movement speed
     * @param {Node} node - Node to draw
     * @param {number} scale - Scale factor
     */
    drawVelocityIndicator(node, scale) {
        const velocity = node.smoothedVelocity || 0;
        const normalizedVel = Math.min(1, velocity / 5);
        
        if (normalizedVel > 0.1) {
            // Draw a motion trail effect
            const trailLength = normalizedVel * 20;
            const angle = Math.atan2(node.vy, node.vx);
            
            const trailGradient = this.ctx.createLinearGradient(
                node.x, node.y,
                node.x - Math.cos(angle) * trailLength,
                node.y - Math.sin(angle) * trailLength
            );
            trailGradient.addColorStop(0, node.baseColor);
            trailGradient.addColorStop(1, 'rgba(0,0,0,0)');
            
            this.ctx.strokeStyle = trailGradient;
            this.ctx.lineWidth = node.radius * 0.3;
            this.ctx.globalAlpha = normalizedVel * 0.5;
            this.ctx.beginPath();
            this.ctx.moveTo(node.x, node.y);
            this.ctx.lineTo(
                node.x - Math.cos(angle) * trailLength,
                node.y - Math.sin(angle) * trailLength
            );
            this.ctx.stroke();
            this.ctx.globalAlpha = 1;
        }
    }

    /**
     * Draw drag preview for instrument being dragged from palette
     * @param {Object} inst - Instrument definition
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    drawDragPreview(inst, x, y) {
        this.ctx.globalCompositeOperation = 'lighter';
        
        this.ctx.beginPath();
        this.ctx.fillStyle = inst.color;
        this.ctx.globalAlpha = 0.5;
        this.ctx.arc(x, y, 20, 0, Math.PI * 2);
        this.ctx.fill();
        this.ctx.strokeStyle = '#fff';
        this.ctx.lineWidth = 2;
        this.ctx.stroke();
        
        this.ctx.globalCompositeOperation = 'source-over';
    }

    /**
     * Perform a complete render frame
     * @param {Array<Node>} nodes - Array of nodes
     * @param {Object} mouse - Mouse state
     * @param {Object|null} draggedInst - Instrument being dragged (or null)
     */
    render(nodes, mouse, draggedInst = null) {
        this.drawBackground();
        this.drawConnections(nodes);
        this.drawNodes(nodes, mouse);
        
        if (draggedInst) {
            this.drawDragPreview(draggedInst, mouse.x, mouse.y);
        }
    }
}

// Export singleton instance
export const canvasRenderer = new CanvasRenderer();
